import React, { useState } from "react";
import styles from "./learningToolImgToWord.module.css";
import LearningToolButtons from "../learningToolButtons/LearningToolButtons";
import LearningUnitButtons from "../learningUnitButtons/LearningUnitButtons";
import LearningToolModal from "../learningToolModal/LearningToolModal";
import ImgToWordModal from "./imgToWordModal/ImgToWordModal";
import axios from "axios";

const apiUrl = process.env.REACT_APP_URL;

axios.defaults.baseURL = `${apiUrl}`;
axios.defaults.withCredentials = true;

const LearningToolImgToWord = ({
  tool,
  mayEditTool,
  removeToolFromList,
  maxUnitsPerTool,
}) => {
  /* The LearningToolImgToWord component is responsible for displaying, adding, editing, removing, and checking answers of an Image to Word Learning Tool. Each tool may contain one or more learning units. An Image to Word learning unit is an exercise in which the user should select an answer that best describes the displayed image. */

  const [userSelections, setUserSelections] = useState({});
  const [userAnswers, setUserAnswers] = useState({});
  const [unitsFeedback, setUnitsFeedback] = useState({});
  const [toolUnits, setToolUnits] = useState(tool.units);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [isNewUnitModalOpen, setIsNewUnitModalOpen] = useState(false);
  const [activeModalUnit, setActiveModalUnit] = useState(null);
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [isUnitContentModalOpen, setIsUnitContentModalOpen] = useState(false);
  const [activeContentModalUnit, setActiveContentModalUnit] = useState(null);
  const [activeUnitIndex, setActiveUnitIndex] = useState(null);

  const learningToolId = tool.learning_tool_id;
  const learningZoneId = tool.learning_zone_id;

  /* Open the new unit modal, to enter details of a new unit */
  const onAddUnit = () => {
    setIsNewUnitModalOpen(true);
    setActiveUnitIndex(null);
  };

  /**
   * Open the confirmation modal, to confirm the deletion of a unit.
   * @param {*} unitId - the id of the unit to be deleted
   */
  const onDeleteUnit = (unitId) => {
    setActiveModalUnit(unitId);
    setIsUnitModalOpen(true);
  };

  /**
   * Open the confirmation modal, to confirm the deletion of the tool.
   */
  const onDeleteTool = () => {
    setIsToolModalOpen(true);
  };

  /* Close the unit delete confirmation modal */
  const closeUnitModal = () => {
    setIsUnitModalOpen(false);
  };

  /* Close the tool delete confirmation modal */
  const closeToolModal = () => {
    setIsToolModalOpen(false);
  };

  /* Close the unit content modal */
  const closeUnitContentModal = () => {
    setIsUnitContentModalOpen(false);
    setIsNewUnitModalOpen(false);
  };

  /* Delete a unit from the database and remove it from the page */
  const handleModalConfirmUnitDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_units/deactivate/${learningToolId}/${activeModalUnit}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        if (toolUnits.length <= 1) {
          removeToolFromList(learningToolId);
        } else {
          setToolUnits((prevUnits) =>
            prevUnits.filter(
              (unit) => unit.learning_unit_id !== activeModalUnit
            )
          );
        }
      } else {
        console.error("Error - unit was not deleted.");
      }
    } catch (error) {
      console.error("Error - unit was not deleted.");
    }

    closeUnitModal();
  };

  /**
   * Delete the learning tool from the database and remove it from the page
   */
  const handleModalConfirmToolDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_tools/deactivate/${learningToolId}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        removeToolFromList(learningToolId);
      } else {
        console.error("Error - tool was not deleted.");
      }
    } catch (error) {
      console.error("Error - tool was not deleted.");
    }

    closeToolModal();
  };

  /**
   * Handle unit solution change by the user (the solving user selected another solution)
   * @param {*} unitId - the id of the unit in which the selection change took place
   * @param {*} solutionIdx - the index of the selected solution
   * @param {*} solutionText - the text of the selected solution
   */
  const handleSelectionChange = (unitId, solutionIdx, solutionText) => {
    setUserSelections((prevSelections) => ({
      ...prevSelections,
      [unitId]: solutionIdx,
    }));

    setUserAnswers((prevAnswers) => ({
      ...prevAnswers,
      [unitId]: solutionText,
    }));
  };

  const handleCheckAnswer = (unitId) => {
    const unitToCheck = toolUnits.find(
      (unit) => unit.learning_unit_id === unitId
    );

    if (!unitToCheck) {
      console.error(`Unit with ID ${unitId} not found.`);
      return;
    }

    const userAnswer = userAnswers[unitId];
    const correctAnswer = getUnitCorrectAnswer(unitId);

    const feedbackClass = userAnswer === correctAnswer ? "✔" : "✘";
    const feedbackType = userAnswer === correctAnswer ? "correct" : "incorrect";

    setUnitsFeedback({
      ...unitsFeedback,
      [unitId]: { symbol: feedbackClass, type: feedbackType },
    });
  };

  /**
   * Check all answers of the tool's units, and set the feedback accordingly
   */
  const handleCheckAllAnswers = () => {
    const updatedFeedback = {};

    toolUnits.forEach((unit) => {
      const unitId = unit.learning_unit_id;
      const userAnswer = userAnswers[unitId] || "";
      const correctAnswer = getUnitCorrectAnswer(unitId);

      updatedFeedback[unitId] = {
        symbol: userAnswer === correctAnswer ? "✔" : "✘",
        type: userAnswer === correctAnswer ? "correct" : "incorrect",
      };
    });

    setUnitsFeedback(updatedFeedback);
  };

  /**
   * Get the correct answer of a given unit
   * @param {*} unitId - the id of the unit to get the correct answer of.
   * @returns string - the correct answer of the unit.
   */
  function getUnitCorrectAnswer(unitId) {
    const reqUnit = toolUnits.find((unit) => unit.learning_unit_id === unitId);

    if (!reqUnit || !reqUnit.unitSolutions) {
      console.error(`Unit with ID ${unitId} not found or has no solutions.`);
      return null;
    }

    const correctAnswer = reqUnit.unitSolutions.find(
      (solution) => solution.solution_text === reqUnit.correct_answer
    );

    if (!correctAnswer) {
      console.error(`Correct answer not found for unit ID ${unitId}.`);
      return null;
    }

    return correctAnswer.solution_text;
  }

  /**
   * Open the unit content modal to allow the user to edit a unit
   * @param {*} unitId - the id of the unit to edit
   * @param {*} idx - the index of the unit in the tool
   */
  const handleEditUnit = (unitId, idx) => {
    setActiveContentModalUnit(unitId);
    setActiveUnitIndex(idx);
    setIsUnitContentModalOpen(true);
  };

  /**
   * Update the page display with an updated unit content
   * @param {*} updatedUnitContent - object - the updated unit data
   */
  const handleUnitContentConfirm = (updatedUnitContent) => {
    setToolUnits((prevUnits) =>
      prevUnits.map((unit) =>
        unit.learning_unit_id === updatedUnitContent.learning_unit_id
          ? { ...unit, ...updatedUnitContent }
          : unit
      )
    );

    closeUnitContentModal();
  };

  /**
   * Add a new unit to the page display
   * @param {*} newUnit - an object with the data of the new unit
   */
  const handleAddUnitToList = (newUnit) => {
    const newUnitObj = {
      ...newUnit.newUnit,
      unitSolutions: newUnit.newUnit.unitSolutions.map((solution, index) => ({
        solution_text: solution.solution_text,
        solution_number_in_unit: index + 1,
        is_active: 1,
        learning_unit_id: newUnit.newUnit.learning_unit_id,
      })),
    };

    setToolUnits((prevUnits) => [...prevUnits, newUnitObj]);

    closeUnitContentModal();
  };

  const BASE_URL = `${apiUrl}/`;

  const toolData = toolUnits.map((unit, idx) => {
    return (
      <div key={idx} className={styles.singleUnit}>
        <div className={styles.unitButtons}>
          <LearningUnitButtons
            mayEditTool={mayEditTool}
            isVoc={false}
            unitId={unit.learning_unit_id}
            onCheckAnswer={handleCheckAnswer}
            onEditUnit={() => handleEditUnit(unit.learning_unit_id, idx)}
            onDeleteUnit={onDeleteUnit}
          />
        </div>
        <div className={styles.questionContainer}>
          <figure>
            <img
              src={
                unit.resource_url
                  ? `${BASE_URL}${unit.resource_url.replace(/\\/g, "/")}`
                  : ""
              }
              alt={`Question ${idx + 1}`}
            />
          </figure>
        </div>

        <div className={styles.answersAndFeedback}>
          <div className={styles.answersContainer}>
            {Array.isArray(unit.unitSolutions) &&
              unit.unitSolutions.map((solution, solutionIdx) => {
                return (
                  <div key={solutionIdx} className={styles.singleAnswer}>
                    <input
                      type="radio"
                      id={"mcu" + unit.learning_unit_id + "s" + solutionIdx}
                      name={"mcu" + unit.learning_unit_id}
                      value={solution.solution_text}
                      checked={
                        userSelections[unit.learning_unit_id] === solutionIdx
                      }
                      onChange={() =>
                        handleSelectionChange(
                          unit.learning_unit_id,
                          solutionIdx,
                          solution.solution_text
                        )
                      }
                    />
                    <label
                      htmlFor={
                        "mcu" + unit.learning_unit_id + "s" + solutionIdx
                      }
                    >
                      {solution.solution_text}
                    </label>
                  </div>
                );
              })}
          </div>
          <p
            className={`${styles.unitFeedback} ${
              styles[unitsFeedback[unit.learning_unit_id]?.type]
            }`}
          >
            {unitsFeedback[unit.learning_unit_id]?.symbol}
          </p>
        </div>
      </div>
    );
  });

  return (
    <div className={styles.imgToWordTool}>
      <div className={styles.toolButtons}>
        {mayEditTool && (
          <LearningToolButtons
            toolId={learningToolId}
            handleAddNewUnit={onAddUnit}
            onDeleteTool={onDeleteTool}
            unitsNum={toolUnits.length}
            maxUnitsPerTool={maxUnitsPerTool}
          />
        )}
      </div>
      <h2>Image to Word Match</h2>
      <p className={styles.creatorInfo}>By {tool.username}</p>
      <p className={styles.instructions}>
        Select the text that best describes the image:
      </p>
      <div className={styles.toolData}>{toolData}</div>
      <div className={styles.checkBtnContainer}>
        <button onClick={handleCheckAllAnswers}>Check All Answers</button>
      </div>
      <LearningToolModal
        message="Are you sure you want to delete this learning unit?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isUnitModalOpen}
        onClose={closeUnitModal}
        onConfirm={handleModalConfirmUnitDelete}
      />

      <LearningToolModal
        message="Are you sure you want to delete this learning tool?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isToolModalOpen}
        onClose={closeToolModal}
        onConfirm={handleModalConfirmToolDelete}
      />

      <ImgToWordModal
        header={`Edit Image to Word Unit ${activeUnitIndex + 1}`}
        unitContent={toolUnits.find(
          (unit) => unit.learning_unit_id === activeContentModalUnit
        )}
        learningToolId={learningToolId}
        approveBtnText="Update"
        cancelBtnText="Cancel"
        show={isUnitContentModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={handleUnitContentConfirm}
      />

      <ImgToWordModal
        header={`Add Image to Word Unit`}
        unitContent={null}
        learningToolId={learningToolId}
        learningZoneId={learningZoneId}
        approveBtnText="Create"
        cancelBtnText="Cancel"
        show={isNewUnitModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={handleAddUnitToList}
      />
    </div>
  );
};

export default LearningToolImgToWord;
